import React, { useState, useRef, useEffect } from "react";
import { makeStyles, useTheme, Popper, Dropdown, MenuIcon } from "@seahorse/ui";
import {
  getDefaultBestXExecutionTimestamp,
  getDefaultBestXExpiryTimestamp,
} from "../BestX/bestxDateDefaults";
import {
  fromZonedDateTimeInputValue,
  toUtcDateTimeValue,
  getDateTimeInputZoneLabel,
  getExecutionTimestampBounds,
  toZonedDateTimeInputValue,
} from "../shared/dateTimeInput";
import DisplayTimeZoneDropdown from "../shared/DisplayTimeZoneDropdown";
import {
  useDisplayTimeZone,
  useResetDisplayTimeZoneOnPairChange,
} from "../../Utils/displayTimeZone";

interface TradeAnalysisPopOverMenuProps {
  setCurrencyPair: (currency: string) => void;
  setUseContext: (useContext: boolean) => void;
  useContext: boolean;
  currencyPair: string;
  currencyList: string[];
  tenorList: string[] | null;
  setTenor: any | null;
  setUserLanguage: any;
  userLanguage: string;
  setExecutionTimestamp: any;
  setExecutionSpotRate: any;
  setExpirationDate: any;
  setExecutionForwardPoints: any;
  tradeType: "BUY" | "SELL";
  setTradeType: (tradeType: "BUY" | "SELL") => void;
  executionTimestamp: string;
  executionSpotRate: number | null;
  executionForwardPoints: number | null;
  expirationTimestamp: string;
}

const TRADE_TYPE_OPTIONS = [
  { label: "BUY", value: "BUY" },
  { label: "SELL", value: "SELL" },
];

const TradeAnalysisPopOverMenu: React.FunctionComponent<
  TradeAnalysisPopOverMenuProps
> = ({
  setCurrencyPair,
  setUseContext,
  useContext,
  currencyPair,
  setUserLanguage,
  userLanguage,
  currencyList,
  tenorList,
  setTenor,
  setExecutionTimestamp,
  setExecutionSpotRate,
  setExpirationDate,
  setExecutionForwardPoints,
  tradeType,
  setTradeType,
  executionTimestamp,
  executionSpotRate,
  executionForwardPoints,
  expirationTimestamp,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  useResetDisplayTimeZoneOnPairChange(currencyPair);
  const [selectedCurrency, setSelectedCurrency] = useState(
    useContext ? "AUTO" : currencyPair || "AUTO"
  );
  const menuIconRef: any = useRef(null);
  const popperRef: any = useRef(null);

  const [selectedTenors, setSelectedTenors]: any = useState([]);

  const [draftTradeType, setDraftTradeType] = useState<"BUY" | "SELL">(tradeType);
  // Draft timestamps are held in UTC; the inputs show the display zone.
  const [draftExecTs, setDraftExecTs] = useState(
    () =>
      executionTimestamp ||
      localStorage.getItem("trade_analysis_exec_ts") ||
      getDefaultBestXExecutionTimestamp()
  );
  const [draftExecRate, setDraftExecRate] = useState(() =>
    executionSpotRate !== null
      ? String(executionSpotRate)
      : localStorage.getItem("trade_analysis_exec_rate") || "157.96"
  );
  const [draftExecFwd, setDraftExecFwd] = useState(() =>
    executionForwardPoints !== null
      ? String(executionForwardPoints)
      : localStorage.getItem("trade_analysis_exec_fwd") || "-0.0002"
  );
  const [draftExpiry, setDraftExpiry] = useState(
    () =>
      expirationTimestamp ||
      localStorage.getItem("trade_analysis_expiry") ||
      getDefaultBestXExpiryTimestamp()
  );
  const { timeZone } = useDisplayTimeZone();
  const draftExecInput = toZonedDateTimeInputValue(draftExecTs, timeZone);
  const draftExpiryInput = toZonedDateTimeInputValue(draftExpiry, timeZone);
  const { minExecutionTimestamp, maxExecutionTimestamp } =
    getExecutionTimestampBounds(timeZone);
  const [draftErrors, setDraftErrors] = useState<Record<string, string>>({
    executionTimestamp: "",
    expirationTimestamp: "",
  });

  const theme = useTheme();
  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      padding: "10px",
      zIndex: 1000,
      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    menuIcon: {
      cursor: "pointer",
      color: theme.globallink.colors.Slate[700],
    },
    popperContainer: {
      padding: "16px",
      backgroundColor: theme.globallink.colors.Slate[900],
      borderRadius: "8px",
      boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.35)",
      maxWidth: "320px",
      zIndex: 1000,
      width: "320px",
      color: "#E0E0E0",
    },
    dropdownContainer: { marginBottom: "12px" },
    title: {
      color: "#E0E0E0",
      fontSize: "14px",
      marginBottom: "6px",
      fontWeight: "bold",
      opacity: 0.9,
    },
    input: {
      width: "100%",
      padding: "10px",
      borderRadius: "8px",
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      outline: "none",
      marginBottom: "12px",
    },
    buttonRow: {
      display: "flex",
      gap: 10,
      justifyContent: "flex-end",
      marginTop: 8,
    },
    button: {
      cursor: "pointer",
      padding: "10px 12px",
      borderRadius: "10px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(80, 170, 255, 0.16)",
      color: "#E0E0E0",
      fontWeight: 700,
    },
    secondaryButton: {
      cursor: "pointer",
      padding: "10px 12px",
      borderRadius: "10px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      fontWeight: 600,
    },
    helper: {
      fontSize: 12,
      opacity: 0.7,
      marginTop: -8,
      marginBottom: 10,
    },
    error: {
      color: "#ff6b6b",
      fontSize: 12,
      marginTop: -8,
      marginBottom: 8,
    },
  });

  const classes = useStyles();

  const [dropDownCurrencyList, setDropDownCurrencyList] = useState([
    { label: "AUTO", value: "AUTO" },
    { label: "USDJPY", value: "USDJPY" },
  ]);

  useEffect(() => {
    if (currencyList !== null) {
      const currencyItems = [...currencyList]
        .sort((a, b) => a.localeCompare(b))
        .map((currency) => ({
          label: currency,
          value: currency,
        }));
      setDropDownCurrencyList([
        { label: "AUTO", value: "AUTO" },
        ...currencyItems,
      ]);
    }
  }, [currencyList]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const insidePopper =
        popperRef.current && popperRef.current.contains(target);
      const insideMenuIcon =
        menuIconRef.current && menuIconRef.current.contains(target);
      if (insidePopper || insideMenuIcon) return;
      const clickedEl = target as HTMLElement;
      if (
        clickedEl.closest?.(
          "[role='listbox'], [role='option'], [role='presentation']"
        )
      )
        return;
      setIsModalOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    return () =>
      document.removeEventListener("mousedown", handleOutsideClick, true);
  }, []);

  useEffect(() => {
    if (tenorList !== null && selectedTenors.length === 0) {
      const mapped = tenorList.map((item) => ({ label: item, value: item }));
      setSelectedTenors(mapped);
      setTenor?.(tenorList);
    }
  }, [tenorList, selectedTenors.length, setTenor]);

  useEffect(() => {
    if (isModalOpen) return;

    setSelectedCurrency(useContext ? "AUTO" : currencyPair || "AUTO");
    setDraftTradeType(tradeType);
    setDraftExecTs(executionTimestamp || "");
    setDraftExecRate(
      executionSpotRate !== null ? String(executionSpotRate) : ""
    );
    setDraftExecFwd(
      executionForwardPoints !== null ? String(executionForwardPoints) : ""
    );
    setDraftExpiry(expirationTimestamp || "");
  }, [
    isModalOpen,
    useContext,
    currencyPair,
    tradeType,
    executionTimestamp,
    executionSpotRate,
    executionForwardPoints,
    expirationTimestamp,
  ]);

  const handleCurrencyChange = (value: any) => {
    setSelectedCurrency(value);
  };

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {
      executionTimestamp: "",
      expirationTimestamp: "",
    };

    if (!draftExecInput) {
      nextErrors.executionTimestamp = "Execution timestamp is required";
    } else if (
      draftExecInput < minExecutionTimestamp ||
      draftExecInput > maxExecutionTimestamp
    ) {
      nextErrors.executionTimestamp =
        "Execution timestamp must be within the last 2 months and not in the future";
    }

    if (
      draftExpiryInput &&
      draftExecInput &&
      draftExpiryInput < draftExecInput
    ) {
      nextErrors.expirationTimestamp =
        "Expiration date must be greater than or equal to execution timestamp";
    }

    if (nextErrors.executionTimestamp || nextErrors.expirationTimestamp) {
      setDraftErrors(nextErrors);
      return;
    }

    setDraftErrors({ executionTimestamp: "", expirationTimestamp: "" });

    if (selectedCurrency !== "AUTO") {
      setCurrencyPair(selectedCurrency);
      setUseContext(false);
    } else {
      setUseContext(true);
    }

    setTradeType(draftTradeType);
    const executionTimestamp = toUtcDateTimeValue(draftExecTs);
    const expirationTimestamp = toUtcDateTimeValue(draftExpiry);

    setExecutionTimestamp(executionTimestamp);

    const parsedExecRate = Number(draftExecRate);
    const parsedExecFwd = Number(draftExecFwd);
    setExecutionSpotRate(Number.isFinite(parsedExecRate) ? parsedExecRate : null);
    setExecutionForwardPoints(Number.isFinite(parsedExecFwd) ? parsedExecFwd : null);
    setExpirationDate(expirationTimestamp);

    localStorage.setItem("trade_analysis_exec_ts", executionTimestamp);
    localStorage.setItem("trade_analysis_exec_rate", draftExecRate);
    localStorage.setItem("trade_analysis_exec_fwd", draftExecFwd);
    localStorage.setItem("trade_analysis_expiry", expirationTimestamp);

    setIsModalOpen(false);
  };

  const handleReset = () => {
    setSelectedCurrency(useContext ? "AUTO" : currencyPair || "AUTO");
    setDraftTradeType(tradeType);
    setDraftExecTs(executionTimestamp || "");
    setDraftExecRate(executionSpotRate !== null ? String(executionSpotRate) : "");
    setDraftExecFwd(
      executionForwardPoints !== null ? String(executionForwardPoints) : ""
    );
    setDraftExpiry(expirationTimestamp || "");
    setDraftErrors({ executionTimestamp: "", expirationTimestamp: "" });
  };

  return (
    <div className={classes.container}>
      <div
        ref={menuIconRef}
        className={classes.menuIcon}
        onClick={() => setIsModalOpen((prev) => !prev)}
      >
        <MenuIcon />
      </div>

      <Popper
        open={isModalOpen}
        anchorEl={menuIconRef.current}
        placement="bottom-start"
        ref={popperRef}
      >
        <div className={classes.popperContainer}>
          {/* Display preference: applies immediately, independent of Submit. */}
          <DisplayTimeZoneDropdown
            currencyPair={currencyPair}
            titleClassName={classes.title}
            containerClassName={classes.dropdownContainer}
          />

          {/* Currency */}
          <div className={classes.title}>Currency Pair</div>
          <div className={classes.dropdownContainer}>
            <Dropdown
              items={dropDownCurrencyList}
              value={selectedCurrency}
              onChange={handleCurrencyChange}
              allowBlankSelection={true}
              placeholder="Select currency"
              fullWidth={true}
            />
          </div>

          {/* Trade Type */}
          <div className={classes.title}>Trade Type</div>
          <div className={classes.dropdownContainer}>
            <Dropdown
              items={TRADE_TYPE_OPTIONS}
              value={draftTradeType}
              onChange={(value: any) => setDraftTradeType(value)}
              placeholder="Select trade type"
              fullWidth={true}
            />
          </div>

          {/* Execution Timestamp */}
          <div className={classes.title}>
            Execution Timestamp ({getDateTimeInputZoneLabel(draftExecTs, timeZone)})
          </div>
          <input
            className={classes.input}
            type="datetime-local"
            step={60}
            min={minExecutionTimestamp}
            max={maxExecutionTimestamp}
            value={draftExecInput}
            onChange={(e) => {
              setDraftExecTs(fromZonedDateTimeInputValue(e.target.value, timeZone));
              setDraftErrors((prev) => ({
                ...prev,
                executionTimestamp: "",
                expirationTimestamp: "",
              }));
            }}
          />
          {draftErrors.executionTimestamp && (
            <div className={classes.error}>{draftErrors.executionTimestamp}</div>
          )}
          <div className={classes.helper}>
            Select date and time directly.
          </div>

          <div className={classes.title}>Execution Spot Rate</div>
          <input
            className={classes.input}
            value={draftExecRate}
            onChange={(e) => setDraftExecRate(e.target.value)}
            placeholder="e.g. 157.96"
            inputMode="decimal"
          />

          <div className={classes.title}>Execution Forward Point</div>
          <input
            className={classes.input}
            value={draftExecFwd}
            onChange={(e) => setDraftExecFwd(e.target.value)}
            placeholder="e.g. -0.0002"
            inputMode="decimal"
          />

          <div className={classes.title}>
            Expiration Date ({getDateTimeInputZoneLabel(draftExpiry, timeZone)})
          </div>
          <input
            className={classes.input}
            type="datetime-local"
            step={60}
            min={draftExecInput || undefined}
            value={draftExpiryInput}
            onChange={(e) => {
              setDraftExpiry(fromZonedDateTimeInputValue(e.target.value, timeZone));
              setDraftErrors((prev) => ({ ...prev, expirationTimestamp: "" }));
            }}
          />
          {draftErrors.expirationTimestamp && (
            <div className={classes.error}>{draftErrors.expirationTimestamp}</div>
          )}

          {/* Submit */}
          <div className={classes.buttonRow}>
            <button
              className={classes.secondaryButton}
              onClick={handleReset}
              type="button"
            >
              Reset
            </button>
            <button
              className={classes.button}
              onClick={handleSubmit}
              type="button"
            >
              Submit
            </button>
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default TradeAnalysisPopOverMenu;
